import { useState } from "react";
import {
	Box,
	Dialog,
	IconButton,
	ImageList,
	ImageListItem,
	Stack,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ReviewPhoto } from "@/domain/eventInstance.interface";

interface Props {
	photos: ReviewPhoto[];
	mode?: "view" | "review";
}

const ZOOM_STEPS = [0.5, 1, 2] as const;

export const PhotoViewer = ({ photos, mode = "view" }: Props) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const [zoomIdx, setZoomIdx] = useState(1);

	if (!photos?.length) {
		return (
			<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
				No photos submitted.
			</Typography>
		);
	}

	const open = (idx: number) => {
		setActiveIndex(idx);
		setZoomIdx(1);
	};

	const close = () => setActiveIndex(null);

	const stepZoom = (delta: number) => {
		setZoomIdx((curr) =>
			Math.max(0, Math.min(ZOOM_STEPS.length - 1, curr + delta)),
		);
	};

	const navigate = (delta: number) => {
		setActiveIndex((curr) => {
			if (curr === null) return curr;
			const next = curr + delta;
			if (next < 0 || next >= photos.length) return curr;
			setZoomIdx(1);
			return next;
		});
	};

	const active = activeIndex !== null ? photos[activeIndex] : null;
	const zoom = ZOOM_STEPS[zoomIdx];

	return (
		<Box>
			<ImageList cols={mode === "review" ? 4 : 3} gap={8} sx={{ m: 0, p: 1 }}>
				{photos.map((photo, idx) => (
					<ImageListItem
						key={photo.key}
						sx={{
							cursor: "pointer",
							borderRadius: 1,
							overflow: "hidden",
							// SIMS-9420: the letterbox around a fitted photo reads as a frame.
							bgcolor: "surface.light",
						}}
						onClick={() => open(idx)}
					>
						<img
							src={photo.url}
							alt={`submission-${idx + 1}`}
							loading="lazy"
							style={{
								width: "100%",
								height: 160,
								// SIMS-9420 (D-9420-1, D-9420-4): both the View submission modal and the
								// Review Queue modal show the whole photo, letterboxed on a neutral
								// backdrop, so nothing is cropped. The click into the larger view is unchanged.
								objectFit: "contain",
							}}
						/>
					</ImageListItem>
				))}
			</ImageList>

			<Dialog open={active !== null} onClose={close} maxWidth="lg" fullWidth>
				{active && (
					<Box sx={{ position: "relative", bgcolor: "black" }}>
						<Stack
							direction="row"
							spacing={1}
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								zIndex: 2,
								color: "white",
							}}
						>
							<IconButton
								onClick={() => stepZoom(-1)}
								disabled={zoomIdx === 0}
								sx={{ color: "white" }}
							>
								<ZoomOutIcon />
							</IconButton>
							<IconButton
								onClick={() => stepZoom(1)}
								disabled={zoomIdx === ZOOM_STEPS.length - 1}
								sx={{ color: "white" }}
							>
								<ZoomInIcon />
							</IconButton>
							<IconButton onClick={close} sx={{ color: "white" }}>
								<CloseIcon />
							</IconButton>
						</Stack>
						{photos.length > 1 && (
							<>
								<IconButton
									onClick={() => navigate(-1)}
									disabled={activeIndex === 0}
									sx={{
										position: "absolute",
										top: "50%",
										left: 8,
										transform: "translateY(-50%)",
										zIndex: 2,
										color: "white",
									}}
								>
									<NavigateBeforeIcon fontSize="large" />
								</IconButton>
								<IconButton
									onClick={() => navigate(1)}
									disabled={activeIndex === photos.length - 1}
									sx={{
										position: "absolute",
										top: "50%",
										right: 8,
										transform: "translateY(-50%)",
										zIndex: 2,
										color: "white",
									}}
								>
									<NavigateNextIcon fontSize="large" />
								</IconButton>
							</>
						)}
						<Box
							sx={{
								height: "75vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "auto",
							}}
						>
							<img
								src={active.url}
								alt="submission"
								style={{
									transform: `scale(${zoom})`,
									transition: "transform 120ms ease-out",
									maxWidth: "100%",
									maxHeight: "100%",
								}}
							/>
						</Box>
						<Stack
							direction="row"
							justifyContent="space-between"
							sx={{
								px: 2,
								py: 1,
								color: "white",
								bgcolor: "rgba(0,0,0,0.6)",
							}}
						>
							<Typography variant="caption">
								Photo {(activeIndex ?? 0) + 1} of {photos.length} ·{" "}
								{Math.round(zoom * 100)}%
							</Typography>
							{active.capturedAt && (
								<Typography variant="caption">
									Captured {new Date(active.capturedAt).toLocaleString()}
								</Typography>
							)}
						</Stack>
					</Box>
				)}
			</Dialog>
		</Box>
	);
};
